import { Easings } from "@scrolly/renderer";

// mostly adapted from https://github.com/nozzle/react-static/blob/57ecc92a63182aab91112be5c3e787c338308b09/packages/react-static/src/browser/utils/scrollTo.js

interface ScrollOptions {
  duration?: number
  offset?: number
  context?: Window | null | HTMLElement
  easing?: (t: number) => number
}

const defaultOptions: Required<ScrollOptions> = {
  duration: 800,
  offset: 0,
  context: typeof document !== 'undefined' ? window : null,
  easing: Easings.cubicInOut,
}

const getTop = (
  element: HTMLElement,
  offset: number,
  contextScrollHeight: number,
  contextVisibleHeight: number
) =>
  Math.min(
    element.getBoundingClientRect().top + window.pageYOffset + offset,
    contextScrollHeight - contextVisibleHeight
  )

const getPosition = (
  start: number,
  end: number,
  elapsed: number,
  duration: number,
  easeFn: (t: number) => number
) => {
  if (elapsed > duration) {
    return end
  }
  return start + (end - start) * easeFn(elapsed / duration)
}

export default function scrollTo(
  target: number | HTMLElement,
  options: ScrollOptions
) {
  const duration = options.duration ?? defaultOptions.duration
  const offset = options.offset ?? defaultOptions.offset
  const context = options.context ?? defaultOptions.context
  const easing = options.easing ?? defaultOptions.easing

  // when context is null, we don't scroll
  if (context === null) {
      return undefined;
  }
  let start = window.pageYOffset

  // context is an HTMLElement
  let innerHeight: TODOAny;
  let scrollHeight: TODOAny;
  if (context !== window) {
      start = (context as HTMLElement).scrollTop;
      innerHeight = (context as HTMLElement).offsetHeight;
      scrollHeight = (context as HTMLElement).scrollHeight;
  } else {
      // context is probably a window
      innerHeight = window.innerHeight;
      scrollHeight = document.body.scrollHeight;
  }
  const clock = Date.now() - 1
  let frameId: number;
  let isCancelled = false;
  let settle: (() => void) | undefined;

  // Tween-Markierung am Container (26.08.2026): die WebKit-Klemme im
  // ScrollyViewer muss laufende Tweens von Browser-Eigenbewegungen
  // unterscheiden koennen — Attribut setzen, am Ende/Abbruch entfernen.
  const markiere = (an: boolean) => {
      if (context !== window && (context as HTMLElement)?.setAttribute) {
          if (an) (context as HTMLElement).setAttribute("data-scrolly-tween", "1");
          else (context as HTMLElement).removeAttribute("data-scrolly-tween");
      }
  };
  markiere(true);

  return {
    promise: new Promise<void>((resolve) => {
      settle = resolve;
      const step = () => {
          if (isCancelled) {
              markiere(false);
              resolve();
              return;
          }

          const elapsed = Date.now() - clock;
          const end =
              typeof target === "number" ? target : getTop(target as HTMLElement, offset, scrollHeight, innerHeight);
          if (context !== window) {
              (context as HTMLElement).scroll(0, getPosition(start, end, elapsed, duration, easing));
          } else {
              window.scroll(0, getPosition(start, end, elapsed, duration, easing));
          }

          if (typeof duration === "undefined" || elapsed > duration) {
              markiere(false);
              resolve();
              return;
          }

          // Sanity check to prevent taking over the scroll once we prematurely got to the element
          if (start === end) {
              markiere(false);
              resolve();
              return;
          }

          frameId = requestAnimationFrame(step);
      };
      frameId = requestAnimationFrame(step);
    }),
    cancel: () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      isCancelled = true;
      // cancelAnimationFrame kappt die Kette — step raeumt dann nicht mehr
      // auf, also Markierung hier direkt entfernen.
      markiere(false);
      // Und die Promise SETTELN: ohne das lief `tween.promise.finally` der
      // Aufrufer nach einem Abbruch nie — paginateSlide liess dadurch
      // `knopfZielIndex` fuer immer stehen, und jede weitere Navigation
      // (Knoepfe wie Pfeiltasten) rechnete vom eingefrorenen Index:
      // wirkte komplett tot. (Fund 30.08.2026 beim Pfeiltasten-Einbau.)
      settle?.();
    }
  };
}
